/** @file bindAttachmentClips: binds each flattened attachment's `att<i>.` clips and driver node to a puppet. */
import type { Clip } from '@zilo/pose3d-format/types/clip';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { attachmentPlans } from '@zilo/pose3d-pose/attach/attachmentPlans';
import type { AttachmentPlan } from '@zilo/pose3d-pose/attach/attachTypes';
import { prepareRig } from '@zilo/pose3d-pose/rig/prepareRig';

/**
 * Decision 20: per `puppet.attachments[i]`, prefix `att<i>.`, the driver's node index (-1 when absent or unknown), the
 * nodes named with the prefix and the given clips whose name starts with it, in the given order. Stores the plans in
 * `attachmentPlans` for `evaluate` and returns them; a puppet without attachments gets `[]`.
 */
export function bindAttachmentClips(puppet: Puppet, clips: readonly Clip[]): AttachmentPlan[] {
  const rig = prepareRig(puppet);
  const plans = (puppet.attachments ?? []).map((attachment, i): AttachmentPlan => {
    const prefix = `att${String(i)}.`;
    return {
      prefix,
      driver: attachment.driver === undefined ? -1 : (rig.index.get(attachment.driver) ?? -1),
      ownNodes: rig.names.flatMap((name, k) => (name.startsWith(prefix) ? [k] : [])),
      clips: clips.filter((clip) => clip.name.startsWith(prefix)),
    };
  });
  attachmentPlans.set(puppet, plans);
  return plans;
}
